<template>
    <v-row>
        <v-dialog v-model="dialog" max-width="800px" scrollable>
            <v-card v-if="dialog">
                <v-card-title>
                    <span class="text-h5">Send Email — {{ position.stock }} | {{ position.company }}</span>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12" sm="5">
                                <v-select
                                    outlined
                                    dense
                                    label="Email type"
                                    v-model="emailType"
                                    :items="emailTypes"
                                    @change="applyTemplate"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="7">
                                <v-text-field
                                    outlined
                                    dense
                                    label="Subject"
                                    v-model="subject"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-textarea
                                    outlined
                                    label="Email body (HTML)"
                                    v-model="body"
                                    rows="14"
                                    class="email-body-input"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="12" md="6">
                                <div class="text-caption grey--text mb-1">Preview</div>
                                <v-card outlined class="email-preview">
                                    <div v-html="previewHtml"></div>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-divider class="my-3"></v-divider>
                        <v-row align="center">
                            <v-col cols="6">
                                <strong>Recipients ({{ recipientCount }})</strong>
                            </v-col>
                            <v-col cols="6" class="text-right">
                                <v-btn text small color="primary" @click="toggleAll(true)">Select all</v-btn>
                                <v-btn text small @click="toggleAll(false)">Clear</v-btn>
                            </v-col>
                        </v-row>
                        <v-progress-linear v-if="loadingPrefill" indeterminate></v-progress-linear>
                        <div v-else class="recipient-list">
                            <div v-if="users.length === 0" class="grey--text py-2">
                                No active paid users matched — add recipients manually below.
                            </div>
                            <v-checkbox
                                v-for="u in users"
                                :key="u.id"
                                v-model="selectedEmails"
                                :value="u.email"
                                dense
                                hide-details
                                class="mt-0"
                            >
                                <template v-slot:label>
                                    <span>{{ u.name }} — {{ u.email }}</span>
                                    <v-chip x-small class="ml-2" :color="u.package === 'PREMIUM' ? 'purple' : 'blue'" text-color="white">
                                        {{ u.package }}
                                    </v-chip>
                                </template>
                            </v-checkbox>
                        </div>
                        <v-combobox
                            v-model="extraEmails"
                            multiple
                            chips
                            deletable-chips
                            outlined
                            dense
                            class="mt-4"
                            label="Additional email addresses"
                            hint="Press enter after each address"
                        ></v-combobox>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="dialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="sending" :disabled="!canSend" @click="send">Send</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { supabase } from '../supabase'

const FALLBACK_LABELS = {
    id: 'ID',
    created_at: 'Created At',
    stock: 'Stock',
    company: 'Company',
    industry: 'Industry',
    mcap: 'Market Cap',
    entry_date: 'Entry Date',
    exit_date: 'Exit Date',
    min_entry: 'Min Entry',
    max_entry: 'Max Entry',
    current_view: 'Current View',
    cmp: 'CMP/CL',
    returns: 'Return',
    package: 'Package',
}

const DATE_FIELDS = ['entry_date', 'exit_date', 'created_at']

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const CTA_STYLE = 'background-color: #0055cc; color: white; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: 600;'

export default {
    name: 'PositionEmail',
    data() {
        return {
            dialog: false,
            position: null,
            loadingPrefill: false,
            sending: false,
            emailType: 'update',
            emailTypes: [
                { text: 'New Position', value: 'new' },
                { text: 'Position Update', value: 'update' },
                { text: 'Position Closed', value: 'closed' },
            ],
            subject: '',
            body: '',
            users: [],
            selectedEmails: [],
            extraEmails: [],
            changeSummary: [],
            labelMap: null,
        }
    },
    computed: {
        recipientCount() {
            return this.selectedEmails.length + this.extraEmails.filter(e => EMAIL_RE.test(String(e).trim())).length
        },
        canSend() {
            return !!this.subject.trim() && !!this.body.trim() && this.recipientCount > 0 && !this.sending
        },
        previewHtml() {
            return `
              <div style="background-color:#f4f8fb;padding:16px 8px;">
                <div style="background:#fff;border-radius:8px;overflow:hidden;max-width:600px;margin:0 auto;box-shadow:0 4px 16px rgba(0,0,0,0.08);">
                  <div style="background-color:#001e60;padding:12px;text-align:center;">
                    <img src="https://vxwnmpzjezjdulnvvqgb.supabase.co/storage/v1/object/public/assets//sigma_logo.PNG" alt="Sigma Equity" width="140" />
                  </div>
                  <div style="padding:20px 16px;color:#222;">${this.body}</div>
                  <div style="padding:12px;text-align:center;background:#eaf0f8;font-size:11px;color:#666;">
                    &copy; 2026 Sigma Equity Research. Grow Together.
                  </div>
                </div>
              </div>`
        },
    },
    methods: {
        async open(item) {
            this.position = item
            this.subject = ''
            this.body = ''
            this.users = []
            this.selectedEmails = []
            this.extraEmails = []
            this.changeSummary = []
            this.dialog = true
            this.loadingPrefill = true
            await Promise.all([this.fetchRecipients(), this.fetchAuditPrefill()])
            this.applyTemplate()
            this.loadingPrefill = false
        },
        async fetchRecipients() {
            const nowIso = new Date().toISOString()
            let query = supabase
                .from('users')
                .select('id, name, email, package')
                .eq('status', 'APPROVED')
                .eq('deleted', false)
                .neq('package', 'TRIAL')
                .lte('starts_on', nowIso)
                .gte('expires_on', nowIso)
                .order('name')
            if (this.position.package === 'P') {
                query = query.eq('package', 'PREMIUM')
            }
            const { data, error } = await query
            if (error) {
                this.$store.dispatch('createAlert', { type: 'error', message: error.message })
                return
            }
            this.users = data
            this.selectedEmails = data.map(u => u.email)
        },
        async fetchAuditPrefill() {
            await this.loadLabels()
            const { data, error } = await supabase
                .from('positions_audit')
                .select('*')
                .eq('position_id', this.position.id)
                .eq('entity', 'position')
                .order('changed_at', { ascending: false })
                .order('id', { ascending: false })
                .limit(10)
            if (error || !data || data.length === 0) {
                this.emailType = this.position.current_view === 'CLOSE' ? 'closed' : 'update'
                return
            }
            // most recent meaningful entry: an INSERT, or an UPDATE with non-cmp changes
            let entry = null
            for (const row of data) {
                if (row.operation === 'INSERT') { entry = row; break }
                if (row.operation === 'UPDATE') {
                    const fields = (row.changed_fields || []).filter(f => f !== 'cmp')
                    if (fields.length > 0) { entry = row; break }
                }
            }
            if (this.position.current_view === 'CLOSE') {
                this.emailType = 'closed'
            } else if (entry && entry.operation === 'INSERT') {
                this.emailType = 'new'
            } else {
                this.emailType = 'update'
            }
            if (entry && entry.operation === 'UPDATE') {
                this.changeSummary = (entry.changed_fields || [])
                    .filter(f => f !== 'cmp')
                    .map(k => ({
                        label: this.fieldLabel(k),
                        oldVal: this.formatValue(k, entry.old_data ? entry.old_data[k] : null),
                        newVal: this.formatValue(k, entry.new_data ? entry.new_data[k] : null),
                    }))
            }
        },
        async loadLabels() {
            if (this.labelMap) return
            const { data, error } = await supabase.from('table_map').select('cno, label')
            if (error) return
            const map = {}
            data.forEach(row => { map[row.cno] = row.label })
            this.labelMap = map
        },
        applyTemplate() {
            const stock = this.position.stock
            const company = this.position.company
            let heading, copy, cta
            if (this.emailType === 'new') {
                this.subject = `📈 New Stock Position Posted — ${stock}`
                heading = 'New Position Alert'
                copy = `A new position has been opened on <strong>${this.escapeHtml(stock)} (${this.escapeHtml(company)})</strong>.<br />
    Check your dashboard now to see the opportunity.`
                cta = 'View Position'
            } else if (this.emailType === 'closed') {
                this.subject = `✅ Position Closed — ${stock}`
                heading = 'Position Closed'
                copy = `The position on <strong>${this.escapeHtml(stock)} (${this.escapeHtml(company)})</strong> has been closed.<br />
    Review the outcome and analysis in your dashboard.`
                cta = 'View Details'
            } else {
                this.subject = `🔔 Position Update — ${stock}`
                heading = 'Position Update'
                copy = `Our view on <strong>${this.escapeHtml(stock)} (${this.escapeHtml(company)})</strong> has been updated.<br />
    Log in to your dashboard for details.`
                cta = 'View Position'
            }
            let changesHtml = ''
            if (this.changeSummary.length > 0 && this.emailType !== 'new') {
                const items = this.changeSummary
                    .map(c => `      <li><strong>${this.escapeHtml(c.label)}</strong>: ${this.escapeHtml(c.oldVal)} &rarr; ${this.escapeHtml(c.newVal)}</li>`)
                    .join('\n')
                changesHtml = `
    <p style="margin-top: 20px; font-weight: 600;">What changed:</p>
    <ul style="padding-left: 20px;">
${items}
    </ul>`
            }
            this.body = `<h2 style="font-weight: 600;">${heading}</h2>
    <p>${copy}</p>${changesHtml}
    <p style="text-align: center; margin-top: 30px;">
      <a href="https://positions.sigmaequityresearch.com/" style="${CTA_STYLE}">${cta}</a>
    </p>`
        },
        toggleAll(on) {
            this.selectedEmails = on ? this.users.map(u => u.email) : []
        },
        fieldLabel(key) {
            return (this.labelMap && this.labelMap[key]) || FALLBACK_LABELS[key] || key
        },
        formatValue(key, val) {
            if (val === null || val === undefined || val === '') return '—'
            if (DATE_FIELDS.includes(key)) return new Date(val).toLocaleDateString('en-IN')
            if (key === 'returns') return (val * 100).toFixed(1) + '%'
            return String(val)
        },
        escapeHtml(str) {
            return String(str)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
        },
        async send() {
            const extras = this.extraEmails.map(e => String(e).trim()).filter(e => e.length > 0)
            const badExtra = extras.find(e => !EMAIL_RE.test(e))
            if (badExtra) {
                this.$store.dispatch('createAlert', { type: 'error', message: `Invalid email address: ${badExtra}` })
                return
            }
            const recipients = [...new Set([...this.selectedEmails, ...extras].map(e => e.toLowerCase()))]
            if (recipients.length === 0) return
            this.sending = true
            const { data, error } = await supabase.functions.invoke('sendPositionEmail', {
                body: {
                    position_id: this.position.id,
                    subject: this.subject,
                    html_body: this.body,
                    recipients,
                },
            })
            this.sending = false
            if (error) {
                let message = error.message
                try {
                    const ctx = await error.context.json()
                    if (ctx.error) message = ctx.error
                } catch (e) { /* keep default message */ }
                this.$store.dispatch('createAlert', { type: 'error', message })
                return
            }
            if (data.sent > 0 && data.failed.length === 0) {
                let message = `Email sent to ${data.sent} recipient(s)`
                if (!data.audit_logged) message += ' (warning: not recorded in change log)'
                this.$store.dispatch('createAlert', { type: 'success', message })
                this.dialog = false
            } else if (data.sent > 0) {
                this.$store.dispatch('createAlert', {
                    type: 'warning',
                    message: `Sent to ${data.sent}, failed for: ${data.failed.map(f => f.email).join(', ')}`,
                })
                // leave only the failed recipients checked for a quick retry
                const failedEmails = data.failed.map(f => f.email)
                this.selectedEmails = this.selectedEmails.filter(e => failedEmails.includes(e.toLowerCase()))
                this.extraEmails = this.extraEmails.filter(e => failedEmails.includes(String(e).trim().toLowerCase()))
            } else {
                this.$store.dispatch('createAlert', {
                    type: 'error',
                    message: `Sending failed: ${data.failed.map(f => f.error)[0] || 'unknown error'}`,
                })
            }
        },
    },
}
</script>

<style scoped>
.recipient-list {
    max-height: 240px;
    overflow-y: auto;
}
.email-preview {
    max-height: 400px;
    overflow-y: auto;
}
.email-body-input textarea {
    font-family: monospace;
    font-size: 12px;
}
</style>
